import dayjs from "dayjs";
import axios from "../axios";
import Compress from "compress.js";
import Resizer from "react-image-file-resizer";
import i18n from "../i18n";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { telegramSupport, lineSupport, whatsappSupport } from "./constants";
import { goBackSafe } from "./appShell";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const isDev = process.env.NODE_ENV !== "production";

export const separator = "#$#!@*(#";

export const getUserPhoneExtensionFromCommunity = (community) => {
  if (community === "sg") {
    return "+65";
  } else if (community === "my") {
    return "+60";
  } else if (community === "hk") {
    return "+852";
  } else if (community === "tw") {
    return "+886";
  } else if (community === "id") {
    return "+62";
  } else if (community === "ph") {
    return "+63";
  } else if (community === "th") {
    return "+66";
  } else if (community === "jp") {
    return "+81";
  } else if (community === "kr") {
    return "+82";
  } else if (community === "au") {
    return "+61";
  } else if (community === "hk") {
    return "+852";
  } else if (community === "cn") {
    return "+86";
  } else if (community === "in") {
    return "+91";
  }

  return "";
};

export const countryToCommunityMap = {
  singapore: "sg",
  malaysia: "my",
  taiwan: "tw",
  indonesia: "id",
  philippines: "ph",
  thailand: "th",
  japan: "jp",
  korea: "kr",
  australia: "au",
  "hong kong": "hk",
  china: "cn",
  india: "in",
};
export const communityToCountryMap = {
  sg: "singapore",
  my: "malaysia",
  tw: "taiwan",
  id: "indonesia",
  ph: "philippines",
  th: "thailand",
  jp: "japan",
  kr: "korea",
  au: "australia",
  hk: "hong kong",
  cn: "china",
  in: "india",
};

export const intMonthToStr = (intMonth) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return months[intMonth - 1];
};

/**

Returns a formatted date string representing the input date in a user-friendly format.
@param {string | Date} date - The input date string or Date object to be formatted.
@returns {string} The formatted date string in the format of "27 Apr 2023".
*/

export const getPrettyDate = (date) => {
  const formattedDate = dayjs(date).format("DD MM YYYY");
  return formattedDate;
};

export const sameDates = (date1, date2) => {
  date1 = dayjs(date1);
  date2 = dayjs(date2);

  return date1.isSame(date2, "day");
};

export const getPrettyDateDayMonth = (dateStr) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const date = new Date(dateStr);
  const day = date.getDate();
  const month = months[date.getMonth()];

  // Function to add ordinal suffix to day
  function getOrdinalSuffix(n) {
    if (n > 3 && n < 21) return "th"; // covers 4th to 20th
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  return `${day}${getOrdinalSuffix(day)} ${month}`;
};

export const dashDateToNiceDate = (date) => {
  if (date) {
    return dayjs(date).format("D MMM YY");
  }
};

export const getTodayDate = () => {
  const dateJs = dayjs().format("DD-MM-YYYY");
  return dateJs;
};

export const getTodayNiceDate = () => {
  return dayjs().format("D MMM YY");
};

export const getEndOfTodayNiceDateTime = () => {
  const locale = localStorage.getItem("locale");

  if (locale && locale.includes("zh")) {
    return dayjs().endOf("day").format("HH:mm, YY年M月D日");
  } else {
    return dayjs().endOf("day").format("HH:mm, D MMM YY");
  }
};

export const getDDMMMDate = (date) => {
  const locale = localStorage.getItem("locale");

  if (dayjs(date).isSame(dayjs(), "year")) {
    if (locale && locale.includes("zh")) {
      return dayjs(date).format("M月D日");
    }
    return dayjs(date).format("D MMM");
  }

  if (locale && locale.includes("zh")) {
    return dayjs(date).format("YY年M月D日");
  }

  return dayjs(date).format("D MMM YY");
};

export const date1BeforeDate2 = (date1, date2) => {
  if (dayjs(date1).isBefore(dayjs(date2))) {
    return true;
  }
  return false;
};

export const date1AfterDate2 = (date1, date2) => {
  if (dayjs(date1).isAfter(dayjs(date2))) {
    return true;
  }
  return false;
};

export const getDayOfWeek = (date) => {
  if (date) {
    return dayjs(date).format("ddd");
  }
};

export const dateRangeFormattingShortYear = (startDate, endDate) => {
  try {
    if (startDate && endDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const locale = localStorage.getItem("locale");

      if (locale.includes("zh")) {
        if (start.isSame(end, "day")) {
          return start.format("YY年M月D日");
        }

        if (start.isSame(end, "month")) {
          return `${start.format("YY年M月D")} - ${end.format("D日")}`;
        }

        if (start.isSame(end, "year")) {
          return `${start.format("YY年M月D日")} - ${end.format("M月D日")}`;
        }

        return `${start.format("YY年M月D日")} - ${end.format("YY年M月D日")}`;
      } else {
        if (start.isSame(end, "day")) {
          return start.format("D MMM YY");
        }

        if (start.isSame(end, "month")) {
          return `${start.format("D")} - ${end.format("D MMM YY")}`;
        }

        if (start.isSame(end, "year")) {
          return `${start.format("D MMM")} - ${end.format("D MMM YY")}`;
        }

        return `${start.format("D MMM YY")} - ${end.format("D MMM YY")}`;
      }
    }

    if (startDate && !endDate) {
      return dayjs(startDate).format("D MMM YY");
    }

    if (!startDate && endDate) {
      return dayjs(endDate).format("D MMM YY");
    }

    return "";
  } catch (error) {
    console.error("Error formatting date range:", error);
    return `${startDate} - ${endDate}`;
  }
};

export const dateRangeFormattingLongYear = (startDate, endDate) => {
  if (startDate && endDate) {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const locale = localStorage.getItem("locale");

    if (locale.includes("zh")) {
      if (start.isSame(end, "day")) {
        return start.format("YYYY年M月D日");
      } else if (start.isSame(end, "month") && start.isSame(end, "year")) {
        return `${start.format("YYYY年M月D")} - ${end.format("D日")}`;
      } else if (start.isSame(end, "year")) {
        return `${start.format("YYYY年M月D日")} - ${end.format("M月D日")}`;
      } else {
        return `${start.format("YYYY年M月D日")} - ${end.format("YYYY年M月D日")}`;
      }
    } else {
      if (start.isSame(end, "day")) {
        return start.format("D MMM YYYY");
      } else if (start.isSame(end, "month") && start.isSame(end, "year")) {
        return `${start.format("D")} - ${end.format("D MMM YYYY")}`;
      } else if (start.isSame(end, "year")) {
        return `${start.format("D MMM")} - ${end.format("D MMM YYYY")}`;
      } else {
        return `${start.format("D MMM YYYY")} - ${end.format("D MMM YYYY")}`;
      }
    }
  }
};

export const getNiceLanguageDate = (date) => {
  const locale = localStorage.getItem("locale");
  if (locale && locale.includes("zh")) {
    return dayjs(date).format("YY年M月D日");
  }
  return dayjs(date).format("D MMM YYYY");
};

export const getShortNiceLanguageDate = (date) => {
  const locale = localStorage.getItem("locale");
  if (locale && locale.includes("zh")) {
    return dayjs(date).format("M月D日");
  }
  return dayjs(date).format("D MMM");
};

export const dateMinusMonths = (date, months) => {
  if (date) {
    date = dayjs(date).subtract(months, "month");
    date = dashDateToNiceDate(date);

    return date;
  }
};

export const translateMonth = (month) => {
  const locale = localStorage.getItem("locale");

  if (locale === "en") {
    return month;
  }

  const monthsEn = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];

  const monthsZh = [
    "一月",
    "二月",
    "三月",
    "四月",
    "五月",
    "六月",
    "七月",
    "八月",
    "九月",
    "十月",
    "十一月",
    "十二月",
  ];

  const index = monthsEn.findIndex((m) => m === month.toLowerCase());
  if (index === -1) return month; // fallback

  if (locale.includes("zh")) {
    // Same for both simplified and traditional
    return monthsZh[index];
  }

  return month;
};

export const dateMinusDays = (date, days) => {
  if (date) {
    date = dayjs(date).subtract(days, "day");
    date = dashDateToNiceDate(date);

    return date;
  }
};

export const dateDiffMilliSeconds = (date1, date2) => {
  date1 = dayjs(date1);
  date2 = dayjs(date2);
  const milliseconds = Math.abs(date1.diff(date2));
  return milliseconds;
};

export const getAge = (birthday, lowerBoundaryAge) => {
  const ageBasedOnYear = dayjs().year() - dayjs(birthday).year();
  const ageBasedOnBirthday = dayjs().diff(dayjs(birthday), "year");

  if (ageBasedOnBirthday === lowerBoundaryAge - 1) {
    return ageBasedOnYear;
  }

  return ageBasedOnBirthday;
};

export const getDateForNextDay = (day) => {
  // 1 is monday, 2 is tuesday, etc
  const dateCopy = new Date();
  const nextDate = new Date(
    dateCopy.setDate(dateCopy.getDate() + ((7 - dateCopy.getDay() + day) % 7 || 7))
  );

  return nextDate;
};

export const userAgeEligibleForTrip = (birthday, age_restriction) => {
  let [startAge, endAge] = age_restriction.split("-");
  startAge = parseInt(startAge);
  endAge = parseInt(endAge);
  const age = getAge(birthday, startAge);

  // 35+ means 30+
  if (startAge === 35) {
    return age >= 30;
  }

  // 18-35 means 18-40
  if (startAge <= age && age <= endAge + 5) {
    return true;
  } else {
    return false;
  }
};

export const userReservedPaymentForTrip = async (userId, tripId) => {
  try {
    const res = await axios.get("/v1/trip/getReserved", {
      params: { userId: userId, tripId: tripId },
    });

    return res.data;
  } catch (err) {
    return {
      isReserved: false,
      bookingPosition: -1,
    };
  }
};

export const objectEmpty = (obj) => {
  return Object.keys(obj).length === 0;
};

export const isNumber = (value) => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

// Replace each tab with 2 spaces
export const replaceTabsWithSpaces = (str) => {
  return str.replace(/\t/g, "  ");
};

export const earlyBirdSlotsRemaining = (
  num_slots_paid,
  total_slots,
  early_bird_percentage = 25,
  late_bird_percentage = 25
) => {
  // EB=0 means the host disabled early-bird pricing — there are no early-bird
  // slots to count. Without this guard, `cap < 0/100` is vacuously false and
  // the fallthrough returns a positive number, causing the listing to render
  // a phantom early-bird tag for trips where EB is off.
  if (!early_bird_percentage) return 0;

  const earlySpots = Math.ceil((total_slots * early_bird_percentage) / 100);
  if (num_slots_paid / total_slots < early_bird_percentage / 100) {
    return earlySpots - num_slots_paid;
  }

  // considered late when left with X% of slots
  const numOfLateSlots = Math.floor((late_bird_percentage / 100) * total_slots);
  const numOfEarlySlots = total_slots - numOfLateSlots;
  const numOfEarlySlotsRemaining = numOfEarlySlots - num_slots_paid;

  return numOfEarlySlotsRemaining > 0 ? numOfEarlySlotsRemaining : 0;
};

export const cloneDeep = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Navigates back one page if there is a page history, otherwise navigates to the specified fallback path.
 * "Page history" is this site's router history, not history.length, and a dedicated app screen
 * opened on this page is closed instead (see goBackSafe in ./appShell).
 *
 * @param {Function} navigate - The navigation function (e.g., from Gatsby or React Router).
 * @param {string} fallbackPath - The fallback path to navigate to if there is no page history.
 */
export const goBackOrNavigate = (navigate, fallbackPath) => goBackSafe(navigate, fallbackPath);

export const isEmptyObject = (obj) => !obj || Object.keys(obj).length === 0;

export const removeDuplicates = (array) => {
  return [...new Set(array)];
};

export const arrayToDict = (array, keyProp, value) => {
  const dict = {};
  for (const obj of array) {
    dict[obj[keyProp]] = value;
  }
  return dict;
};

export const objectToQueryParams = (obj) => {
  if (!obj) {
    return "";
  }

  return Object.keys(obj)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]))
    .join("&");
};

export const resizeImageFn = async (file, maxSide) => {
  const compress = new Compress();

  const resizedImage = await compress.compress([file], {
    size: 1, // the max size in MB, defaults to 2MB
    quality: 1, // the quality of the image, max is 1,
    maxWidth: maxSide ?? 512, // the max width of the output image, defaults to 1920px
    maxHeight: maxSide ?? 512, // the max height of the output image, defaults to 1920px
    resize: true, // defaults to true, set false if you do not want to resize the image width and height
  });

  const img = resizedImage[0];
  const base64str = img.data;
  const imgExt = img.ext;
  const resizedBlob = Compress.convertBase64ToFile(base64str, imgExt);
  return resizedBlob;
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const makeAppLink = (url, params) => {
  //need to replace sotravel with exp+sotravelapp-dev for local dev
  const slug = "sotravel";
  let link = `${slug}://${url}`;
  if (params && typeof params === "object") {
    link += "?";
    Object.entries(params).forEach(([k, v], i) => {
      if (i !== 0) link += "&";
      link += `${k}=${v}`;
    });
  }
  return link;
};

// example input is "1400"
export const convertMilitaryToCivilianTime = (militaryTime, offsetMinutes = 0) => {
  // Create dayjs object from military time string
  const time = dayjs()
    .hour(Number(militaryTime.slice(0, 2)))
    .minute(Number(militaryTime.slice(2)))
    .second(0);

  // Add any offset minutes
  const offsetTime = time.add(offsetMinutes, "minute");

  // Format to civilian time with am/pm
  return offsetTime.format("h:mm a");
};

export const calculateMilitaryTimeDifference = (startTime, endTime) => {
  // Create dayjs objects from military time strings
  const startTimeObj = dayjs()
    .hour(parseInt(startTime.slice(0, 2)))
    .minute(parseInt(startTime.slice(2)))
    .second(0);

  const endTimeObj = dayjs()
    .hour(parseInt(endTime.slice(0, 2)))
    .minute(parseInt(endTime.slice(2)))
    .second(0);

  // Get duration using relative time
  return startTimeObj.to(endTimeObj, true);
};

export const resizeImage = (file, width, height, quality) => {
  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      file, // Is the file of the image which will resized.
      width, // Is the maxWidth of the resized new image.
      height, // Is the maxHeight of the resized new image.
      "WEBP", // Is the compressFormat of the resized new image.
      quality, // Is the quality of the resized new image 0-100
      0, // Is the degree of clockwise rotation to apply to uploaded image.
      (uri) => {
        resolve(uri);
      }, // Is the callBack function of the resized new image URI.
      "base64", // Is the output type of the resized new image.
      width, // Is the minWidth of the resized new image.
      height // Is the minHeight of the resized new image.
    );
  });
};

export const getNextFridayDate = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysUntilNextFriday =
    dayOfWeek === 5 ? 7 : dayOfWeek < 5 ? 5 - dayOfWeek : 5 + (7 - dayOfWeek);
  const nextFriday = new Date(today);
  nextFriday.setDate(today.getDate() + daysUntilNextFriday);
  const nextFridayFormatted = nextFriday.toISOString().split("T")[0];
  return nextFridayFormatted;
};

export const convertUTCToLocalDateTimeString = (utcDate, community) => {
  const locale = localStorage.getItem("locale");

  if (!utcDate) return "";

  const timezones = {
    sg: "Asia/Singapore",
    hk: "Asia/Hong_Kong",
    tw: "Asia/Taipei",
    my: "Asia/Kuala_Lumpur",
    ph: "Asia/Manila",
    id: "Asia/Jakarta",
    th: "Asia/Bangkok",
    jp: "Asia/Tokyo",
    kr: "Asia/Seoul",
  };

  const timezone = timezones[community] || "Asia/Singapore"; // Default to Singapore

  if (locale && locale.includes("zh")) {
    return dayjs(utcDate).tz(timezone).format("M月D日 h:mm A"); // Chinese format
  } else {
    return dayjs(utcDate).tz(timezone).format("MMMM D, h:mm A"); // Format like "January 1, 3:30 PM"
  }
};

export const isNonEngCountry = (country, community) =>
  country === "taiwan" ||
  country === "thailand" ||
  country === "japan" ||
  country === "china" ||
  country === "korea" ||
  community === "tw" ||
  community === "jp" ||
  community === "th" ||
  community === "cn" ||
  community === "kr";

export const getSupportLink = (community) => {
  let supportLink = telegramSupport;
  if (community === "tw") {
    supportLink = lineSupport;
  } else if (community === "my") {
    supportLink = whatsappSupport;
  }

  return supportLink;
};

export const primaryLoginsToShow = (community) => {
  if (community === "tw" || community === "jp" || community === "th") {
    return "line";
  }

  if (community === "cn" || community === "kr" || community === "ae") {
    return "email";
  }

  return "telegram";
};
